import {BaseApi} from './BaseApi'

// ============================================================================
// TYPES
// ============================================================================

export interface UnpaidEorItem {
  agencyId: string
  agencyName: string
  taxPaymentGroupId: string | null
  invoiceCount: number
  employeeCount: number
  dateRangeStart: string
  dateRangeEnd: string
  pendingInvoiceCount: number
  statePaid: boolean
  federalPaid: boolean
  statePaidAt?: string
  statePaidBy?: string
  federalPaidAt?: string
  federalPaidBy?: string
}

export interface UnpaidEorListResponse {
  eors: UnpaidEorItem[]
}

export interface PaidGroupItem {
  taxPaymentGroupId: string
  agencyId: string
  agencyName: string
  invoiceCount: number
  employeeCount: number
  dateRangeStart: string
  dateRangeEnd: string
  statePaidAt: string
  statePaidBy: string
  federalPaidAt: string
  federalPaidBy: string
  completedAt: string
}

export interface PaidGroupListResponse {
  groups: PaidGroupItem[]
}

export interface MarkPaidRequest {
  agencyId: string
  taxPaymentGroupId?: string
}

export interface MarkPaidResponse {
  taxPaymentGroupId: string
  paidAt: string
  status: string
  batchCount: number
}

export interface ReportByDateRangeParams {
  startDate: string // ISO format
  endDate: string // ISO format
  agencyId: string // Required for platform admin
}

export interface ReportByGroupParams {
  groupId: string // Tax Payment Group ID
}

// ============================================================================
// TAX API CLASS
// ============================================================================

class TaxApi extends BaseApi {
  /**
   * Get list of EORs with unpaid or partially paid tax groups
   */
  async getUnpaidEors(): Promise<UnpaidEorListResponse> {
    const response = await this.get<UnpaidEorListResponse>('/payroll/tax-payments/unpaid')
    return response.data
  }

  /**
   * Get list of fully paid tax payment groups
   */
  async getPaidGroups(): Promise<PaidGroupListResponse> {
    const response = await this.get<PaidGroupListResponse>('/payroll/tax-payments/paid')
    return response.data
  }

  /**
   * Mark state taxes as paid for an EOR
   */
  async markStatePaid(request: MarkPaidRequest): Promise<MarkPaidResponse> {
    const response = await this.post<MarkPaidResponse>(
      '/payroll/tax-payments/mark-state-paid',
      request,
    )
    return response.data
  }

  /**
   * Mark federal taxes as paid for an EOR
   */
  async markFederalPaid(request: MarkPaidRequest): Promise<MarkPaidResponse> {
    const response = await this.post<MarkPaidResponse>(
      '/payroll/tax-payments/mark-federal-paid',
      request,
    )
    return response.data
  }

  // ============================================================================
  // REPORT DOWNLOADS
  // ============================================================================

  /**
   * Download Check Summary Report by date range
   * Returns a CSV file
   */
  async downloadCheckSummaryByDateRange(params: ReportByDateRangeParams): Promise<Blob> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
      agencyId: params.agencyId,
    })
    const response = await this.get<Blob>(
      `/payroll/reports/check-summary/by-date-range?${queryParams.toString()}`,
      {responseType: 'blob'},
    )
    return response.data
  }

  /**
   * Download Check Summary Report by tax payment group
   * Returns a CSV file
   */
  async downloadCheckSummaryByGroup(params: ReportByGroupParams): Promise<Blob> {
    const response = await this.get<Blob>(
      `/payroll/reports/check-summary/by-group/${params.groupId}`,
      {responseType: 'blob'},
    )
    return response.data
  }

  /**
   * Download Tax Summary Report by date range
   * Returns a CSV file
   */
  async downloadTaxSummaryByDateRange(params: ReportByDateRangeParams): Promise<Blob> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
      agencyId: params.agencyId,
    })
    const response = await this.get<Blob>(
      `/payroll/reports/tax-summary/by-date-range?${queryParams.toString()}`,
      {responseType: 'blob'},
    )
    return response.data
  }

  /**
   * Download Tax Summary Report by tax payment group
   * Returns a CSV file
   */
  async downloadTaxSummaryByGroup(params: ReportByGroupParams): Promise<Blob> {
    const response = await this.get<Blob>(
      `/payroll/reports/tax-summary/by-group/${params.groupId}`,
      {responseType: 'blob'},
    )
    return response.data
  }
}

// ============================================================================
// HELPER: Trigger file download from blob
// ============================================================================

export const downloadBlobAsFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const taxApi = new TaxApi()
export default taxApi

